
import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useLanguage } from '@/hooks/useLanguage';
import { Separator } from '@/components/ui/separator';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { LeaksSearchBar } from './leaks/LeaksSearchBar';
import { LeaksGrid } from './leaks/LeaksGrid';
import { LeaksPagination } from './leaks/LeaksPagination';

interface LeakItem {
  domain: string;
  breach_date: string;
  data_leaked: string;
  data_leaked_pl: string;
  leak_count: number;
  description: string;
  description_pl: string;
}

// Items per page
const ITEMS_PER_PAGE = 9;

export function LeaksFeed() {
  const { language, t } = useLanguage();
  const [leaks, setLeaks] = useState<LeakItem[]>([]);
  const [filteredLeaks, setFilteredLeaks] = useState<LeakItem[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  // Pagination state
  const [searchParams, setSearchParams] = useSearchParams();
  const initialPage = parseInt(searchParams.get('page') || '1', 10);
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [totalPages, setTotalPages] = useState(1);

  // Update URL when page changes
  useEffect(() => {
    setSearchParams(prev => {
      prev.set('page', currentPage.toString());
      return prev;
    });
  }, [currentPage, setSearchParams]);

  // Fetch leaks data from Supabase
  useEffect(() => {
    const fetchLeaks = async () => {
      setLoading(true);
      setError(null);

      try {
        // Fetch the leak.json file from Supabase storage
        const { data, error: downloadError } = await supabase
          .storage
          .from('dane')
          .download('leak.json');

        if (downloadError) {
          throw new Error(`Error downloading file: ${downloadError.message}`);
        }

        // Parse the JSON data
        const jsonData = await data.text();
        const leaksData: LeakItem[] = JSON.parse(jsonData);

        // Sort by breach date (newest first)
        const sortedLeaks = leaksData.sort((a, b) =>
          new Date(b.breach_date).getTime() - new Date(a.breach_date).getTime()
        );

        // Update the state with the fetched leaks
        setLeaks(sortedLeaks);
        setFilteredLeaks(sortedLeaks);
        setTotalPages(Math.ceil(sortedLeaks.length / ITEMS_PER_PAGE));
        setLoading(false);
      } catch (err) {
        console.error('Error fetching leaks:', err);
        setError('Failed to load leak data');
        setLoading(false);

        toast({
          title: "Error loading leak data",
          description: "Could not load the leak data. Please try again later.",
          variant: "destructive",
        });
      }
    };

    fetchLeaks();
  }, [toast]);

  // Handle search functionality
  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredLeaks(leaks);
      setTotalPages(Math.ceil(leaks.length / ITEMS_PER_PAGE));
      setTotalPages(Math.ceil(leaks.length / ITEMS_PER_PAGE));
      // Don't reset page if it's valid, otherwise reset to 1
      if (currentPage > Math.ceil(leaks.length / ITEMS_PER_PAGE)) {
        setCurrentPage(1);
      }
      return;
    }

    const lowercaseQuery = searchQuery.toLowerCase();
    const filtered = leaks.filter(leak => {
      const domainMatch = leak.domain.toLowerCase().includes(lowercaseQuery);
      const descriptionMatch = (language === 'en'
        ? leak.description.toLowerCase().includes(lowercaseQuery)
        : leak.description_pl.toLowerCase().includes(lowercaseQuery));
      const dataLeakedMatch = (language === 'en'
        ? leak.data_leaked.toLowerCase().includes(lowercaseQuery)
        : leak.data_leaked_pl.toLowerCase().includes(lowercaseQuery));

      return domainMatch || descriptionMatch || dataLeakedMatch;
    });

    setFilteredLeaks(filtered);
    setTotalPages(Math.ceil(filtered.length / ITEMS_PER_PAGE));
    setTotalPages(Math.ceil(filtered.length / ITEMS_PER_PAGE));
    setCurrentPage(1); // Reset to page 1 on search
  }, [searchQuery, leaks, language]);

  // Format date to user's language preference
  const formatDate = (dateString: string) => {
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    };

    try {
      return new Date(dateString).toLocaleDateString(
        language === 'en' ? 'en-US' : 'pl-PL',
        options
      );
    } catch (e) {
      console.error('Error formatting date:', e);
      return dateString;
    }
  };

  // Format large numbers with commas/spaces according to locale
  const formatNumber = (num: number) => {
    return new Intl.NumberFormat(
      language === 'en' ? 'en-US' : 'pl-PL'
    ).format(num);
  };

  // Get current page data
  const getCurrentItems = () => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    return filteredLeaks.slice(startIndex, endIndex);
  };

  return (
    <section className="py-20 bg-secondary/50 dark:bg-secondary/20">
      <div className="container px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold tracking-tight sm:text-4xl text-foreground">
            {t('leaks.title')}
          </h2>
          <p className="mt-4 text-lg text-muted-foreground max-w-3xl mx-auto">
            {t('leaks.subtitle')}
          </p>
          <Separator className="mt-8 mb-12 mx-auto w-24 bg-primary/50" />
        </div>

        {/* Search input */}
        <LeaksSearchBar
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          leaksCount={leaks.length}
          filteredCount={filteredLeaks.length}
        />

        {/* Leaks Grid */}
        <LeaksGrid
          items={getCurrentItems()}
          formatDate={formatDate}
          formatNumber={formatNumber}
          isLoading={loading}
          error={error}
        />

        {/* Pagination */}
        <LeaksPagination
          currentPage={currentPage}
          totalPages={totalPages}
          setCurrentPage={setCurrentPage}
        />
      </div>
    </section>
  );
}
